import { useCallback, useState } from "react";
import type { Channel } from "../../shared/host";
import { logEvent } from "../../telemetry";

const STORAGE_KEY_PREFIX = "aiva_pinned_modules";
const MAX_PINS = 2;

// Matches useCommandPalette.ts's STORAGE_REASON_* convention.
const STORAGE_REASON_READ_ERROR = "pinned_modules_read_error";
const STORAGE_REASON_WRITE_ERROR = "pinned_modules_write_error";
const STORAGE_REASON_CLEANUP_ERROR = "pinned_modules_cleanup_error";

// Reports localStorage failures without disrupting the caller; pinning stays a no-op fallback.
function reportStorageError(
  reason: typeof STORAGE_REASON_READ_ERROR | typeof STORAGE_REASON_WRITE_ERROR | typeof STORAGE_REASON_CLEANUP_ERROR,
): void {
  logEvent("connector.action.invoke", "warn", { connectorSystemName: "skills-pinned-modules", reason });
}

// Keyed per host so pins don't leak across Office hosts (AIVA-5171, same fix as chatHistoryStorage.ts).
function storageKey(host: Channel | null): string {
  return `${STORAGE_KEY_PREFIX}:${host ?? "default"}`;
}

// Dead keys from before host-scoping existed; cleaned up on load so DevTools doesn't show stale entries.
const LEGACY_KEYS = [
  STORAGE_KEY_PREFIX,
  `${STORAGE_KEY_PREFIX}_outlook`,
  `${STORAGE_KEY_PREFIX}_word`,
  `${STORAGE_KEY_PREFIX}_powerpoint`,
  `${STORAGE_KEY_PREFIX}_default`,
];

function cleanupLegacyKeys() {
  try {
    for (const key of LEGACY_KEYS) localStorage.removeItem(key);
  } catch (e) {
    console.debug("[usePinnedModules] legacy key cleanup failed:", e instanceof Error ? e.message : String(e));
    reportStorageError(STORAGE_REASON_CLEANUP_ERROR);
  }
}

function loadPinned(host: Channel | null): string[] {
  cleanupLegacyKeys();
  try {
    const stored = localStorage.getItem(storageKey(host));
    if (stored) return JSON.parse(stored) as string[];
  } catch (e) {
    console.debug("[usePinnedModules] localStorage read failed:", e instanceof Error ? e.message : String(e));
    reportStorageError(STORAGE_REASON_READ_ERROR);
  }
  return [];
}

function savePinned(host: Channel | null, ids: string[]) {
  try {
    localStorage.setItem(storageKey(host), JSON.stringify(ids));
  } catch (e) {
    console.debug("[usePinnedModules] localStorage write failed:", e instanceof Error ? e.message : String(e));
    reportStorageError(STORAGE_REASON_WRITE_ERROR);
  }
}

interface UsePinnedModulesReturn {
  pinnedModuleIds: string[];
  togglePin: (moduleId: string) => void;
  isPinned: (moduleId: string) => boolean;
  /** Max modules a user may pin — surfaced so the UI can phrase the limit message. */
  maxPins: number;
  /**
   * True when the last pin attempt was rejected because the ceiling was already
   * reached. Drives the "you can pin a maximum of N modules" popup (AIVA-7299).
   * Cleared on the next successful pin/unpin, or via {@link dismissPinLimit}
   * once the popup auto-dismisses.
   */
  pinLimitReached: boolean;
  /** Clears {@link pinLimitReached} — called when the popup times out or is closed. */
  dismissPinLimit: () => void;
}

export function usePinnedModules(host: Channel | null): UsePinnedModulesReturn {
  const [pinnedModuleIds, setPinnedModuleIds] = useState<string[]>(() => loadPinned(host));
  const [pinLimitReached, setPinLimitReached] = useState(false);

  const togglePin = useCallback((moduleId: string) => {
    setPinnedModuleIds(prev => {
      let next: string[];
      if (prev.includes(moduleId)) {
        next = prev.filter(id => id !== moduleId);
      } else if (prev.length < MAX_PINS) {
        next = [...prev, moduleId];
      } else {
        // AIVA-7299: at the pin ceiling — flag it so the UI can pop a message
        // instead of silently ignoring the click.
        setPinLimitReached(true);
        return prev;
      }
      setPinLimitReached(false);
      savePinned(host, next);
      return next;
    });
  }, [host]);

  const isPinned = useCallback((moduleId: string) => pinnedModuleIds.includes(moduleId), [pinnedModuleIds]);
  const dismissPinLimit = useCallback(() => setPinLimitReached(false), []);

  return { pinnedModuleIds, togglePin, isPinned, maxPins: MAX_PINS, pinLimitReached, dismissPinLimit };
}
